import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { talents } from '../../services/apiService';
import {
  Bot,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  X,
  Clock,
  Sparkles,
  RefreshCw,
  Mic,
  Unplug,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Loader } from '../UI/LoadingSpinner';
import ConfirmDialog from '../UI/ConfirmDialog';
import { TextField, Modal, Select, Checkbox, Button } from '@anyemp/ui-kit';
import { useAuth } from '../../hooks/useAuth';

const AISettingsTab = forwardRef(({ talentId, talentType, showAddButton = true }, ref) => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // Определяем resource для проверки пермишенов
  const aiSettingsResource = useMemo(() => {
    if (talentType) {
      return `${talentType}_ai_settings`;
    }
    return 'ai_settings';
  }, [talentType]);

  // Проверяем права доступа
  const canCreate = useMemo(() => hasPermission('talent-service', aiSettingsResource, 'create'), [hasPermission, aiSettingsResource]);
  const canUpdate = useMemo(() => hasPermission('talent-service', aiSettingsResource, 'update'), [hasPermission, aiSettingsResource]);
  const canDelete = useMemo(() => hasPermission('talent-service', aiSettingsResource, 'delete'), [hasPermission, aiSettingsResource]);

  // Query для завантаження AI настроек
  const {
    data: aiSettings = [],
    isLoading: loading,
    error: queryError
  } = useQuery({
    queryKey: ['talent-ai-settings', talentId],
    queryFn: async () => {
      if (!talentId) return [];

      const response = await talents.getTalentAiSettings(talentId);

      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      throw new Error(response.error || 'Failed to fetch AI settings');
    },
    enabled: !!talentId,
    staleTime: 1000 * 60 * 5, // 5 хвилин cache
    retry: 1
  });

  // Чи вже є голосовий агент — кнопку Generate тоді не показуємо (повторна генерація = no-op).
  const hasAgent = useMemo(() => aiSettings.some((s) => s.ai_voice_agent_id), [aiSettings]);

  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [editingForm, setEditingForm] = useState({ ai_voice_agent_id: '', voice_id: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteAgentConfirm, setDeleteAgentConfirm] = useState(null);
  // Модалка regenerate з вибором опцій
  const [regenerateModal, setRegenerateModal] = useState(null);
  const [regenOptions, setRegenOptions] = useState({ regenerateVoice: false, model: '' });

  // Обробка помилок з useQuery
  useEffect(() => {
    if (queryError) {
      setError(queryError.message || 'Error loading AI settings');
    }
  }, [queryError]);

  // Mutation для создания AI настройки
  const createMutation = useMutation({
    mutationFn: (data) => talents.addTalentAiSetting(talentId, data),
    onSuccess: () => {
      toast.success('AI setting created successfully!');
      setShowForm(false);
      setEditingSettings(null);
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to create AI setting';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  // Mutation для обновления AI настройки
  const updateMutation = useMutation({
    mutationFn: ({ settingId, data }) => talents.updateTalentAiSetting(talentId, settingId, data),
    onSuccess: () => {
      toast.success('AI setting updated successfully!');
      setShowForm(false);
      setEditingSettings(null);
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to update AI setting';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  // Mutation для удаления AI настройки
  const deleteMutation = useMutation({
    mutationFn: (settingId) => talents.deleteTalentAiSetting(talentId, settingId),
    onSuccess: () => {
      toast.success('AI setting deleted successfully!');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to delete AI setting';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  // Mutation для автогенерации ElevenLabs агента (клон голоса + prompt + агент)
  const generateMutation = useMutation({
    mutationFn: (options = {}) => talents.generateTalentAgent(talentId, options),
    onSuccess: (response) => {
      const created = response?.data?.created;
      toast.success(created ? 'Voice agent created!' : 'Agent already exists (use Regenerate to update)');
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to generate voice agent';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  // Mutation для регенерации существующего агента
  const regenerateMutation = useMutation({
    mutationFn: (options = {}) => talents.regenerateTalentAgent(talentId, options),
    onSuccess: () => {
      toast.success('Voice agent updated!');
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to regenerate voice agent';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  // Mutation для удаления агента (вместе с клоном голоса)
  const deleteAgentMutation = useMutation({
    mutationFn: () => talents.deleteTalentAgent(talentId, { deleteVoice: true }),
    onSuccess: () => {
      toast.success('Voice agent deleted!');
      setDeleteAgentConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['talent-ai-settings', talentId] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to delete voice agent';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  });

  const handleCreate = () => {
    setEditingSettings({ id: 'new' });
    setEditingForm({ ai_voice_agent_id: '', voice_id: '' });
    setShowForm(true);
  };

  const handleGenerateAgent = () => {
    setError(null);
    generateMutation.mutate({});
  };

  useImperativeHandle(ref, () => ({
    openForm: handleCreate,
    generateAgent: handleGenerateAgent,
    isGenerating: generateMutation.isPending
  }));

  const handleEdit = (setting) => {
    setEditingSettings(setting);
    setEditingForm({
      ai_voice_agent_id: setting.ai_voice_agent_id || '',
      voice_id: setting.voice_id || ''
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSettings(null);
    setEditingForm({ ai_voice_agent_id: '', voice_id: '' });
  };

  const handleSubmit = () => {
    const data = {
      ai_voice_agent_id: editingForm.ai_voice_agent_id?.trim() || null,
      voice_id: editingForm.voice_id?.trim() || null
    };
    if (editingSettings && editingSettings.id !== 'new') {
      updateMutation.mutate({ settingId: editingSettings.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const openRegenerate = (setting) => {
    setRegenOptions({ regenerateVoice: false, model: '' });
    setRegenerateModal(setting);
  };

  const runRegenerate = () => {
    regenerateMutation.mutate(
      {
        regenerateVoice: regenOptions.regenerateVoice,
        ...(regenOptions.model ? { model: regenOptions.model } : {})
      },
      { onSuccess: () => setRegenerateModal(null) }
    );
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed')
    );
  };

  const handleDelete = (setting) => {
    setDeleteConfirm(setting);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {showAddButton && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary flex items-center">
              <Bot className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              AI Settings
              {aiSettings.length > 0 && (
                <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary ml-2">
                  ({aiSettings.length})
                </span>
              )}
            </h3>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
              Manage AI voice agent settings for this talent
            </p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              <Plus className="h-4 w-4" />
              {createMutation.isPending ? 'Creating...' : 'Add AI Setting'}
            </Button>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 dark:text-red-300 hover:text-red-600 dark:hover:text-red-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Busy banner — показуємо, коли триває дія над агентом */}
      {(generateMutation.isPending || regenerateMutation.isPending || deleteAgentMutation.isPending) && (
        <div className="flex items-center gap-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-3 py-2 text-sm text-violet-700 dark:text-violet-300">
          <RefreshCw className="h-4 w-4 animate-spin shrink-0" />
          <span>
            {generateMutation.isPending && 'Creating voice agent (cloning voice, generating prompt)…'}
            {regenerateMutation.isPending && 'Regenerating agent…'}
            {deleteAgentMutation.isPending && 'Deleting agent…'}
          </span>
        </div>
      )}

      {/* Generate Voice Agent — visible in content whenever there is no agent yet */}
      {!hasAgent && canCreate && !generateMutation.isPending && !showForm && (
        <Button
          size="small"
          color="tertiary"
          onClick={handleGenerateAgent}
          title="Auto-create voice agent: clone voice + generate prompt + create ElevenLabs agent"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Generate Voice Agent
        </Button>
      )}

      {/* New setting form */}
      {showForm && editingSettings?.id === 'new' && (
        <div className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 mb-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
          <div className="space-y-3">
            <TextField
              label="AI Voice Agent ID"
              size="small"
              fullWidth
              type="text"
              value={editingForm.ai_voice_agent_id}
              onChange={(e) => setEditingForm((f) => ({ ...f, ai_voice_agent_id: e.target.value }))}
              placeholder="agent_... (ElevenLabs agent ID)"
              autoFocus
            />
            <TextField
              label="Voice ID"
              size="small"
              fullWidth
              type="text"
              value={editingForm.voice_id}
              onChange={(e) => setEditingForm((f) => ({ ...f, voice_id: e.target.value }))}
              placeholder="ElevenLabs voice ID (optional)"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="small" onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
            <Button size="small" variant="outlined" color="neutral" onClick={handleCancel} disabled={createMutation.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* AI Settings list */}
      {aiSettings.length === 0 && !showForm ? (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-4">No AI settings configured yet</p>
      ) : (
        <div className="space-y-3">
          {aiSettings.map((setting) => (
            <div key={setting.id} className={`border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary transition-opacity ${(regenerateMutation.isPending || deleteAgentMutation.isPending) && editingSettings?.id !== setting.id ? 'opacity-60' : ''}`}>
              {editingSettings?.id === setting.id ? (
                <div className="space-y-3">
                  <TextField
                    label="AI Voice Agent ID"
                    size="small"
                    fullWidth
                    type="text"
                    value={editingForm.ai_voice_agent_id}
                    onChange={(e) => setEditingForm((f) => ({ ...f, ai_voice_agent_id: e.target.value }))}
                    placeholder="agent_... (ElevenLabs agent ID)"
                    autoFocus
                  />
                  <TextField
                    label="Voice ID"
                    size="small"
                    fullWidth
                    type="text"
                    value={editingForm.voice_id}
                    onChange={(e) => setEditingForm((f) => ({ ...f, voice_id: e.target.value }))}
                    placeholder="ElevenLabs voice ID (optional)"
                  />
                  <div className="flex gap-2">
                    <Button size="small" onClick={handleSubmit} disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="small" variant="outlined" color="neutral" onClick={handleCancel} disabled={updateMutation.isPending}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0 space-y-2">
                    {/* Agent ID */}
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-primary-500 shrink-0" title="Agent ID" />
                      {setting.ai_voice_agent_id ? (
                        <>
                          <code className="text-xs font-mono text-light-text-primary dark:text-dark-text-primary truncate">{setting.ai_voice_agent_id}</code>
                          <button onClick={() => copyToClipboard(setting.ai_voice_agent_id)} className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded" title="Copy agent ID">
                            <Copy className="h-3 w-3" />
                          </button>
                          <a href={`https://elevenlabs.io/app/talk-to?agent_id=${setting.ai_voice_agent_id}`} target="_blank" rel="noreferrer" className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded inline-flex items-center gap-0.5" title="Talk to agent (ElevenLabs)">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </>
                      ) : (
                        <span className="italic text-xs text-light-text-tertiary dark:text-dark-text-tertiary">No agent ID</span>
                      )}
                    </div>

                    {/* Voice ID */}
                    {setting.voice_id && (
                      <div className="flex items-center gap-2">
                        <Mic className="h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" title="Voice ID" />
                        <code className="text-xs font-mono text-light-text-secondary dark:text-dark-text-secondary truncate">{setting.voice_id}</code>
                        <button onClick={() => copyToClipboard(setting.voice_id)} className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded" title="Copy voice ID">
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                      {setting.last_generated_at && (
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Generated: {formatDateTime(setting.last_generated_at)}</span>
                      )}
                      {!setting.last_generated_at && setting.created_at && (
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDateTime(setting.created_at)}</span>
                      )}
                    </div>
                  </div>
                  {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-1 shrink-0">
                      {canUpdate && setting.ai_voice_agent_id && (
                        <button onClick={() => openRegenerate(setting)} disabled={regenerateMutation.isPending} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50" title="Regenerate agent (choose options)">
                          <RefreshCw className={`w-3.5 h-3.5 ${regenerateMutation.isPending ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      {canDelete && setting.ai_voice_agent_id && (
                        <button onClick={() => setDeleteAgentConfirm(setting)} disabled={deleteAgentMutation.isPending} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-orange-600 dark:hover:text-orange-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50" title="Delete agent from ElevenLabs (agent + cloned voice)">
                          {deleteAgentMutation.isPending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {canUpdate && (
                        <button onClick={() => handleEdit(setting)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(setting)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={confirmDelete}
          title="Delete AI Setting"
          message={`Are you sure you want to delete AI Setting #${deleteConfirm.id}? This action cannot be undone.`}
          confirmText="Delete"
          confirmStyle="danger"
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Delete agent confirmation dialog */}
      {deleteAgentConfirm && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setDeleteAgentConfirm(null)}
          onConfirm={() => deleteAgentMutation.mutate()}
          title="Delete voice agent"
          message={`This will delete the ElevenLabs agent (${deleteAgentConfirm.ai_voice_agent_id}) and its cloned voice, and clear them from this talent. The talent will lose the Talk button on the site until regenerated. Continue?`}
          confirmText="Delete agent"
          confirmStyle="danger"
          isLoading={deleteAgentMutation.isPending}
        />
      )}

      {/* Regenerate options modal */}
      <Modal
        open={!!regenerateModal}
        onClose={() => !regenerateMutation.isPending && setRegenerateModal(null)}
        size="small"
        closeOnBackdropClick={!regenerateMutation.isPending}
        closeOnEscape={!regenerateMutation.isPending}
      >
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw className="h-5 w-5 text-primary-500" />
          <h3 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary">Regenerate agent</h3>
        </div>
        <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-4">Choose what to update for this talent's voice agent.</p>

        <div className="space-y-3">
            {/* Prompt — always on (backend regenerates prompt on every call) */}
            <Checkbox
              checked
              isDisabled
              label={<span>Update prompt <span className="text-light-text-tertiary dark:text-dark-text-tertiary">(regenerated from profile — always)</span></span>}
            />

            {/* Re-clone voice */}
            <Checkbox
              checked={regenOptions.regenerateVoice}
              onChange={(checked) => setRegenOptions((o) => ({ ...o, regenerateVoice: checked }))}
              isDisabled={regenerateMutation.isPending}
              label={<span>Re-clone voice <span className="text-light-text-tertiary dark:text-dark-text-tertiary">(from talent's current audio)</span></span>}
            />

            {/* Model */}
            <Select
              label="Model (prompt generation)"
              fullWidth
              size="small"
              value={regenOptions.model}
              onChange={(value) => setRegenOptions((o) => ({ ...o, model: typeof value === 'string' ? value : '' }))}
              options={[
                { label: 'Default (gemini-2.5-flash)', value: '' },
                { label: 'google/gemini-2.5-flash', value: 'google/gemini-2.5-flash' },
                { label: 'anthropic/claude-sonnet-4-6', value: 'anthropic/claude-sonnet-4-6' },
                { label: 'openai/gpt-4o-mini', value: 'openai/gpt-4o-mini' },
                { label: 'openai/gpt-4o', value: 'openai/gpt-4o' },
              ]}
            />
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <Button size="small" variant="outlined" color="neutral" onClick={() => setRegenerateModal(null)} disabled={regenerateMutation.isPending}>
              Cancel
            </Button>
            <Button size="small" onClick={runRegenerate} disabled={regenerateMutation.isPending}>
              {regenerateMutation.isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
              {regenerateMutation.isPending ? 'Running...' : 'Run'}
            </Button>
          </div>
      </Modal>
    </div>
  );
});

export default AISettingsTab;
